import {faChevronLeft, faChevronRight} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Select} from 'antd';

import {useLocale} from '../../provider/locale/locale-hook';
import {classNames} from '../../util/css';
import {CursorPaginationHookType} from '../../util/cursor-pagination/cursor-pagination-type';

import * as styles from './cursor-paginator.scss';

type PropsType = {
    cursorPagination: CursorPaginationHookType;
    pageSizeOptions: [number, ...Array<number>];
    lastPage?: number;
    hasNextPage?: boolean;
    className?: string;
};

export function CursorPaginator(props: PropsType): JSX.Element {
    const {cursorPagination, pageSizeOptions, lastPage, hasNextPage, className} = props;
    const {
        pageSize,
        previousPageCursor,
        nextPageCursor,
        changePage,
        changePagePagination,
        updatePageSize,
        changeResultPage,
        resultPage,
        nextPage,
        page,
    } = cursorPagination;

    const {getLocalizedString} = useLocale();

    function nextPageHandler() {
        changePage(nextPageCursor || '');
        changeResultPage(resultPage + 1);

        if (nextPage) {
            changePagePagination(page + 1);
        }
    }

    function previousPageHandler() {
        changePage(previousPageCursor || '');
        changePagePagination(page - 1);
        changeResultPage(resultPage - 1);
    }

    function getIsNextPageDisabled(): boolean {
        // An explicit answer from the data source always wins over the guesswork below.
        if (typeof hasNextPage === 'boolean') {
            return !hasNextPage;
        }

        // `lastPage` comes from a trustworthy total count, so reaching it means there is nothing after it.
        if (lastPage) {
            return lastPage <= resultPage;
        }

        return nextPageCursor === '';
    }

    const isNextPageDisabled: boolean = getIsNextPageDisabled();

    return (
        <div className={classNames(styles.Pagination, className)}>
            <Button
                className={styles.Pagination_button}
                disabled={!previousPageCursor && resultPage - 1 <= 0}
                onClick={previousPageHandler}
            >
                <FontAwesomeIcon fixedWidth icon={faChevronLeft} />
            </Button>

            <Button className={styles.Pagination_button} disabled={isNextPageDisabled} onClick={nextPageHandler}>
                <FontAwesomeIcon fixedWidth icon={faChevronRight} />
            </Button>

            <Select
                className={styles.Pagination_select}
                labelInValue
                onChange={(newValue) => {
                    updatePageSize(typeof newValue === 'object' ? newValue.value : newValue);
                }}
                value={{
                    value: pageSize,
                    label: getLocalizedString('TABLE__PAGINATION__PAGE_SIZE', {pageSize: pageSize.toString()}),
                }}
            >
                {pageSizeOptions.map((pageSizeOption) => (
                    <Select.Option key={pageSizeOption} value={pageSizeOption}>
                        {getLocalizedString('TABLE__PAGINATION__PAGE_SIZE', {pageSize: pageSizeOption.toString()})}
                    </Select.Option>
                ))}
            </Select>
        </div>
    );
}
